import {
  ReactNode, useEffect, useRef,
} from 'react';
import styled, { FlattenInterpolation, ThemeProps } from 'styled-components';
import { useGlobalFocusElement } from '../../util';
import { Flex, FlexColumn } from '../Flex';
import { Button } from '..';
import { Icon } from '../Nav';
import { ThemeType } from '../../styling';

export type ModalSize = 'sm' | 'lg';

export const ModalPrimaryButton = styled(Button)`${({ theme }) => `
  background: ${theme.body};
  color: ${theme.textPrimary};
  border: ${theme.borderWidth[1]} solid ${theme.borderDark};
`}`;

export const ModalSecondaryButton = styled(Button)`${({ theme }) => `
  background: ${theme.button};
  color: ${theme.textSecondary};
`}`;

const ModalBlock = styled(Flex)`${() => `
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: fixed;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  opacity: 1;
  z-index: 99999;
`}`;

const ModalOverlay = styled.a`${({ theme }) => `
  background-color: ${theme.colours.neutral[2]};
  opacity: 0.75;
  bottom: 0;
  cursor: default;
  display: block;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`}`;

const ModalCloseIcon = styled(Icon) <{
  css?: FlattenInterpolation<ThemeProps<ThemeType>>, titleColor?: string
}>`${({ theme, titleColor }) => `
  float: right;
  text-decoration: none;
  cursor: pointer;
  margin-left: ${theme.padding[6]};
  font-size: ${theme.fontSize[2]};
  color: ${titleColor || theme.colours.primary[1]};
  font-size: ${theme.fontSize[3]};
`};
  
  ${({ css }) => css};
`;

const ModalContainer = styled(FlexColumn) <{ size: ModalSize }>`${({ theme, size }) => `
  background: ${theme.colours.neutral[10]};
  border-radius: ${theme.padding[2]};
  max-width: ${size === 'sm' ? theme.smModalWidth : ''};
  max-width: ${size === 'lg' ? theme.lgModalWidth : ''};
  width: 100%;
  z-index: 1;
  box-shadow: 0px ${theme.padding[2]} ${theme.padding[2]} -${theme.padding[1]} rgba(16, 24, 40, 0.04), 0px ${theme.padding[5]} ${theme.padding[6]} -${theme.padding[1]} rgba(16, 24, 40, 0.10); 
`}`;

const ModalHeader = styled.div<{
  css?: FlattenInterpolation<ThemeProps<ThemeType>>,
  backgroundColor?: string, titleColor?: string
}>`
  ${({ theme, backgroundColor, titleColor }) => `
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.padding[3]} ${theme.padding[6]};
    padding-right: ${theme.padding[5]};
    background-color: ${backgroundColor || theme.colours.primary[10]};
    color: ${titleColor || theme.colours.primary[1]};
    border-top-right-radius: inherit;
    border-top-left-radius: inherit;
    box-shadow: 0px 1px 0px 0px #F2F2F2;
`};
  
  ${({ css }) => css};
`;

const IconWrapper = styled.div<{ titleColor?: string }>`${({ theme, titleColor }) => `
  width: ${theme.fontSize[7]};
  height: ${theme.fontSize[7]};
  color: ${titleColor};
  margin-right: ${theme.padding[2]};
`}`;

const ModalTitle = styled.span`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[3]};
  line-height: ${theme.fontSize[7]};
  display: flex;
`}`;

export const ModalBodyTitle = styled.span`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
  display: flex;
`}`;

export const ModalBodyContent = styled.div`${({ theme }) => `
  font-weight: ${theme.fontWeight.normal};
  margin-top: ${theme.padding[2]};
  display: flex;
`}`;

export const ModalBody = styled.div<{ css?: FlattenInterpolation<ThemeProps<ThemeType>> | string, hasFooter?: boolean }>`${({
  theme, css, hasFooter,
}) => `
  word-break: break-word;
  padding: ${theme.padding[6]};
  font-size: ${theme.fontSize[2]};
  line-height: ${theme.fontSize[6]};
  color: ${theme.colours.neutral[2]};

  ${!hasFooter && `
    border-bottom-right-radius: inherit;
    border-bottom-left-radius: inherit;
  `};

  ${hasFooter && `
    padding-bottom: 0;
  `};

  &::-webkit-scrollbar {
    display: none;
  };
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */

  ${css || ''}
`}`;

export const ModalFooter = styled(Flex) <{
  css?: FlattenInterpolation<ThemeProps<ThemeType>> | string
}>`${({ theme, css }) => `
  gap: ${theme.padding[4]};
  padding: ${theme.padding[6]};
  justify-content: end;
  border-bottom-right-radius: inherit;
  border-bottom-left-radius: inherit;
  ${css || ''};
`}`;

export type TypeButtonModal = {
  label: string,
  action: () => void,
  disabled?: boolean,
}

export type ModalColorType = {
  headerBackground?: string;
  headerTitle?: string;
  background?: string;
}

export type ModalProps = {
  title: string | ReactNode,
  titleIcon?: ReactNode,
  children?: ReactNode,
  footer?: ReactNode,
  isOpen: boolean,
  hideClose?: boolean,
  preventOutsideClose?: boolean,
  hideModal?: () => void,
  xClicked?: () => void,
  size?: ModalSize,
  headerCss?: FlattenInterpolation<ThemeProps<ThemeType>>,
  closeIconCss?: FlattenInterpolation<ThemeProps<ThemeType>>,
  modalColor?: ModalColorType,
  cssChildren?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  cssFooter?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
}

export function Modal({
  title,
  titleIcon,
  children,
  isOpen,
  preventOutsideClose = false,
  hideModal,
  xClicked,
  size = 'lg',
  headerCss,
  closeIconCss,
  modalColor,
  hideClose = false,
  footer,
  cssChildren,
  cssFooter,
}: ModalProps) {
  const xClickedOveride = xClicked || hideModal;

  const handleClickOutSideModal = () => {
    if (preventOutsideClose) return;
    hideModal?.();
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
  }, [isOpen]);

  const modalContainerRef = useRef<HTMLDivElement>(null);
  const lastFocusedElRef = useRef<HTMLElement | null>(null);

  const focusedEl = useGlobalFocusElement();

  useEffect(() => {
    if (isOpen && modalContainerRef) {
      // Save last focus element. So when modal is closed, focus back to it.
      lastFocusedElRef.current = document.activeElement as HTMLElement;
      modalContainerRef?.current?.focus();
    } else {
      lastFocusedElRef.current?.focus();
    }
  }, [isOpen, modalContainerRef]);

  useEffect(() => {
    // Should only focus for these elements within opened modal until modal is closed.
    if (isOpen && focusedEl) {
      const isInside = modalContainerRef.current?.contains(focusedEl);
      if (!isInside) {
        modalContainerRef?.current?.focus();
      }
    }
  }, [isOpen, focusedEl]);

  return (
    <>
      {isOpen && (
        <ModalBlock ref={modalContainerRef} tabIndex={-1}>
          {/* Fix this - use shared click outside handler */}
          <ModalOverlay id="btn-close-modal" onClick={() => handleClickOutSideModal()} />

          <ModalContainer size={size}>
            {/* Modal Header */}
            <ModalHeader
              css={headerCss}
              backgroundColor={modalColor?.headerBackground}
              titleColor={modalColor?.headerTitle}
            >
              <ModalTitle>
                {titleIcon && (
                  <IconWrapper titleColor={modalColor?.headerTitle}>
                    {titleIcon}
                  </IconWrapper>
                )}
                {title}
              </ModalTitle>
              {!hideClose && (
                <ModalCloseIcon
                  className="mi-close"
                  css={closeIconCss}
                  titleColor={modalColor?.headerTitle}
                  onClick={() => xClickedOveride?.()}
                />
              )}
            </ModalHeader>

            {/* Modal Body */}
            <ModalBody css={cssChildren} hasFooter={!!footer}>
              {children}
            </ModalBody>

            {/* Modal Footer */}
            {footer && (<ModalFooter css={cssFooter}>{footer}</ModalFooter>)}
          </ModalContainer>
        </ModalBlock>
      )}
    </>
  );
}
